import React, { useEffect, useCallback, useState, useContext } from "react";
import {
  Heading,
  VStack,
  HStack,
  Pressable,
  Text,
  Spinner,
  Center,
} from "native-base";
import { Helmet } from "react-helmet-async";
import colors from "../../../constants/colors";
import API from "../../../api";
import moment from "moment";
import { StateContext, StatusContext } from "../../../contexts";
import DatePickerCustomInput from "../../../components/datePickerCustomInput";
import DatePicker from "react-datepicker";
import { useSearchParams } from "react-router-dom";
import { Chart } from "react-google-charts";
import qs from "qs";

function AgencyDashboardPage() {
  const state = useContext(StateContext);
  const { updateErrorMessage } = useContext(StatusContext);
  const [tours, setTours] = useState(0);
  const [quotations, setQuotations] = useState(0);
  const [finishedQuotations, setFinishedQuotations] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [initialDate, setInitialDate] = useState(
    searchParams.get("initialDate")
      ? moment(searchParams.get("initialDate")).toDate()
      : moment().subtract(7, "days").startOf("day").toDate(),
  );
  const [finalDate, setFinalDate] = useState(
    searchParams.get("finalDate")
      ? moment(searchParams.get("finalDate")).toDate()
      : moment().add(7, "days").endOf("day").milliseconds(0).toDate(),
  );

  const getTours = useCallback(async () => {
    try {
      setIsLoading(true);
      const query = qs.stringify({
        itinerary: {
          type: "origin",
          isReturn: false,
          departureDate: {
            $gte: initialDate.toISOString(),
            $lte: finalDate.toISOString(),
          },
        },
        agency: { id: state.user.agency.id },
        status: true,
      });
      const response = await API(state.jwt).get(`/tours/?${query}`);
      setTours(response.data.meta.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      updateErrorMessage(error);
    }
  }, [
    finalDate,
    initialDate,
    state.jwt,
    state.user.agency.id,
    updateErrorMessage,
  ]);

  const getQuotations = useCallback(async () => {
    try {
      setIsLoading(true);
      const query = qs.stringify({
        itinerary: {
          type: "origin",
          isReturn: false,
          departureDate: {
            $gte: initialDate.toISOString(),
            $lte: finalDate.toISOString(),
          },
        },
        agency: { id: state.user.agency.id },
        finished: false,
      });
      const response = await API(state.jwt).get(`quotations/?${query}`);
      setQuotations(response.data.meta.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      updateErrorMessage(error);
    }
  }, [
    finalDate,
    initialDate,
    state.jwt,
    state.user.agency.id,
    updateErrorMessage,
  ]);

  const getFinishedQuotations = useCallback(async () => {
    try {
      setIsLoading(true);
      const query = qs.stringify({
        itinerary: {
          type: "origin",
          isReturn: false,
          departureDate: {
            $gte: initialDate.toISOString(),
            $lte: finalDate.toISOString(),
          },
        },
        agency: { id: state.user.agency.id },
        finished: true,
      });
      const response = await API(state.jwt).get(`quotations/?${query}`);
      setFinishedQuotations(response.data.meta.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      updateErrorMessage(error);
    }
  }, [
    finalDate,
    initialDate,
    state.jwt,
    state.user.agency.id,
    updateErrorMessage,
  ]);

  useEffect(() => {
    getTours();
    getQuotations();
    getFinishedQuotations();
  }, [getFinishedQuotations, getQuotations, getTours]);

  const onApplyFilters = () => {
    getQuotations();
    getTours();
    getFinishedQuotations();
  };

  return (
    <VStack>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <VStack paddingY={8} space={5}>
        <Heading color={colors.secondary} fontSize="2xl">
          Dashboard
        </Heading>
        <HStack space={5} alignItems="flex-end" zIndex={1}>
          <DatePicker
            selected={initialDate}
            onChange={setInitialDate}
            customInput={<DatePickerCustomInput label="Rango inicial" />}
            maxDate={finalDate}
            dateFormat="yyyy-MM-dd"
          />
          <DatePicker
            selected={finalDate}
            onChange={setFinalDate}
            customInput={<DatePickerCustomInput label="Rango inicial" />}
            minDate={initialDate}
            dateFormat="yyyy-MM-dd"
          />
          <Pressable
            backgroundColor={colors.primary}
            paddingX={4}
            paddingY={1}
            borderRadius={5}
            onPress={onApplyFilters}
            disabled={isLoading}>
            <Text color={colors.white}>Aplicar</Text>
          </Pressable>
        </HStack>
      </VStack>
      {isLoading ? (
        <Center height={400}>
          <Spinner size="lg" color={colors.primary} />
        </Center>
      ) : (
        <VStack space={5}>
          <Chart
            chartType="Bar"
            data={[
              ["Mes", "Ventas"],
              ["Marzo", 10000],
              ["Abril", 20000],
              ["Mayo", 5000],
              ["Junio", 8000],
              ["Julio", 30000],
              ["Agosto", 20000],
            ]}
            options={{ chart: { title: "Ventas mensuales" } }}
            width="100%"
            height="250px"
            loader={<Spinner />}
          />
          <Chart
            chartType="PieChart"
            data={[
              ["Estado", "Número de cotizaciones"],
              ["Sin terminar", quotations],
              ["Terminadas", finishedQuotations],
            ]}
            options={{ title: "Cotizaciones" }}
            width="100%"
            height="250px"
            loader={<Spinner />}
          />
          <Chart
            chartType="PieChart"
            data={[
              ["", ""],
              ["Cotizaciones", quotations + finishedQuotations],
              ["Tours", tours],
            ]}
            options={{ title: "Cotizaciones vs Tours" }}
            width="100%"
            height="250px"
            loader={<Spinner />}
          />
        </VStack>
      )}
    </VStack>
  );
}

export default AgencyDashboardPage;
